import { translate } from '@/plugins/i18n.js'

const PDP_CONFIG = 'robots,redirect_url,canonical_for,alternates'
const PDP_CONFIG_V1 = 'embed=awards,bundle_items,expert_reviews'

const getFirstAvailableStock = (product) =>
  isApiProduct(product)
    ? _getApiStock(product.stocks)
    : _getMetaStock(product.delivery)

const isInStock = (product) => {
  if (isApiProduct(product)) {
    return !!getFirstAvailableStock(product)
  }
  return product.delivery?.is_in_stock
}

const getUnitPrice = async (product) => {
  if (!isApiProduct(product)) return product.price.price_per_unit_formatted

  const { UtilityHelper, filters } = await _getModules()
  const unit = product.reference_unit.toUpperCase()

  return UtilityHelper.formatString(
    '{0} ({1} {2})',
    await getPricePerUnit(
      product.final_price,
      product.reference_unit_contents,
      unit
    ),
    filters.float(product.reference_unit_contents),
    unit
  )
}

const getPricePerUnit = async (price, contents, unit) => {
  const { UtilityHelper, filters } = await _getModules()
  const pricePerUnit = price / contents
  return UtilityHelper.formatString(
    '{0}/{1}',
    filters.currency(pricePerUnit),
    unit
  )
}

const getSavings = async (product) => {
  const { UtilityHelper } = await _getModules()

  if (!isApiProduct(product)) return product.price.savings.percentage

  return product.original_price !== product.final_price
    ? UtilityHelper.formatString(
        '-%s%',
        Math.round(
          (product.original_price - product.final_price) /
            (product.original_price / 100)
        )
      )
    : null
}

/**
 * Builds the priceObject the FE renders. The BE-resolved `final_price` is
 * the source of truth for the active price — it already encodes
 * special_price + window + admin endless-rule + admin preview_date. The FE
 * just reflects it and recomputes the per-unit price + savings from the
 * resolved value so they stay in sync (bulk's pre-formatted
 * price.price_per_unit_formatted is clock-based and may disagree).
 *
 * Two shapes flow in:
 *  - API products (e.g. OPC PDP fetch): flat `final_price` + `original_price`.
 *  - MetaProducts (collection page tiles + hero, cart upsell, etc.): nested
 *    `price` from the bulk endpoint, with a flat `final_price` overlaid by
 *    productViewItemMixin / HeroTile when it came in via /v2 search.
 */
const createPriceObject = async (product) => {
  const { TranslationsHelper, SiteConfig } = await _getModules()

  if (isApiProduct(product)) {
    return {
      finalPrice: product.final_price,
      finalPriceFormatted: TranslationsHelper.formatCurrency(
        product.final_price
      ),
      originalPrice: product.original_price,
      unitPrice: await getUnitPrice(product),
      savings: await getSavings(product),
      validUntil: product.valid_until
        ? new Date(product.valid_until).toISOString()
        : null,
      showUnitPrice: product.unit_type !== 'piece',
      currency: SiteConfig.currencyConfig.currency,
      showCountdown: product.show_countdown
    }
  }

  // MetaProduct shape. Prefer the BE-resolved `final_price` (search) when
  // present; fall back to bulk's `price.final` for surfaces that bypass v2
  // (cart upsell modal, recently-viewed, etc.).
  const beResolvedFinal =
    typeof product.final_price === 'number' ? product.final_price : null
  const finalPrice = beResolvedFinal ?? product.price.final
  const originalPrice = product.price.original
  const beOverridesBulk =
    beResolvedFinal !== null && beResolvedFinal !== product.price.final

  return {
    finalPrice,
    finalPriceFormatted: beOverridesBulk
      ? TranslationsHelper.formatCurrency(finalPrice)
      : product.price.final_formatted,
    originalPrice,
    unitPrice: beOverridesBulk
      ? await _recomputeMetaUnitPrice(product, finalPrice)
      : product.price.price_per_unit_formatted,
    savings: beOverridesBulk
      ? _computeSavingsPercent(originalPrice, finalPrice)
      : product.price.savings.percentage,
    validUntil: product.price.valid_until
      ? new Date(product.price.valid_until).toISOString()
      : null,
    currency: SiteConfig.currencyConfig.currency,
    showUnitPrice: true,
    showCountdown: product.price.show_countdown
  }
}

const _computeSavingsPercent = (original, final) => {
  if (original <= 0 || original === final) return null
  return `-${Math.round((original - final) / (original / 100))}%`
}

/**
 * Recomputes the meta-product per-unit price string for a different final
 * price by parsing the BE-formatted `price_per_unit_formatted` to recover
 * the contents amount + unit, then dividing the new price by that amount.
 * Falls back to the server-formatted string when parsing fails.
 */
const _recomputeMetaUnitPrice = async (product, price) => {
  const fallback = product.price?.price_per_unit_formatted
  if (!fallback) return fallback

  const match = fallback.match(/\(\s*([\d.,]+)\s+(\S+?)\s*\)\s*$/)
  if (!match) return fallback

  const amountStr = match[1]
  const unit = match[2]
  const lastDot = amountStr.lastIndexOf('.')
  const lastComma = amountStr.lastIndexOf(',')
  const decimalAt = Math.max(lastDot, lastComma)
  const normalized =
    decimalAt === -1
      ? amountStr
      : amountStr.slice(0, decimalAt).replace(/[.,]/g, '') +
        '.' +
        amountStr.slice(decimalAt + 1)
  const amount = Number.parseFloat(normalized)
  if (!Number.isFinite(amount) || amount <= 0) return fallback

  const { UtilityHelper } = await _getModules()
  return UtilityHelper.formatString(
    '{0} ({1} {2})',
    await getPricePerUnit(price, amount, unit),
    amountStr,
    unit
  )
}

const createPriceObjectForUpsell = async (product) => {
  const { SiteConfig, filters } = await _getModules()
  const formatted = translate('fe_app_pdp_final_price_format', 0)

  const unitPrice = (product.price.price_per_unit_formatted ?? '').replace(
    /[^/]+(.+)/,
    `${filters.currency(0)}$1`
  )
  return {
    finalPrice: 0,
    finalPriceFormatted: formatted,
    originalPrice: product.price.final,
    unitPrice: unitPrice,
    savings: '-100%',
    validUntil: new Date(Date.now() + 100000).toISOString(),
    currency: SiteConfig.currencyConfig.currency,
    showUnitPrice: true,
    showCountdown: false
  }
}

const getCartPayload = (product, qty, index, itemListId) => {
  if (isApiProduct(product)) {
    return {
      qty,
      itemListId,
      index,
      productId: +product.product_id,
      campaignId: +product.campaign_id,
      name: product.name,
      price: product.final_price,
      originalPrice: product.original_price,
      variant: product.campaign_name
    }
  }

  return {
    qty,
    itemListId,
    index,
    productId: +product.product_id,
    campaignId: +product.campaign_id,
    name: product.name,
    price: product.final_price,
    originalPrice: product.original_price,
    variant: product.ga_data.variant
  }
}

const isApiProduct = (product) =>
  Object.hasOwn(product, 'campaign_id') && Object.hasOwn(product, 'is_opc')
const isMetaProduct = (product) => Object.hasOwn(product, 'recommendation')

export {
  createPriceObject,
  createPriceObjectForUpsell,
  getCartPayload,
  getFirstAvailableStock,
  getPricePerUnit,
  getSavings,
  getUnitPrice,
  isApiProduct,
  isInStock,
  isMetaProduct,
  PDP_CONFIG,
  PDP_CONFIG_V1
}

const _getApiStock = (stocks) => stocks.find((stock) => stock.is_in_stock)

const _getMetaStock = (delivery) =>
  delivery?.is_in_stock ? { is_in_stock: true, is_enabled: true } : null

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: SiteConfig },
      { default: UtilityHelper },
      { default: TranslationsHelper },
      { default: filters }
    ] = await Promise.all([
      await import('@/services/SiteConfig'),
      await import('@/services/helpers/UtilityHelper'),
      await import('@/services/helpers/TranslationsHelper'),
      await import('@/services/Filters')
    ])
    cache = {
      SiteConfig,
      UtilityHelper,
      TranslationsHelper,
      filters
    }
    return cache
  }
})()
